import {
  startOfMonth,
  endOfMonth,
  isWithinInterval,
  subDays,
  startOfYear,
  startOfQuarter,
} from "date-fns";
import type { Deal } from "../types";

export type PeriodFilter =
  | "all"
  | "this_month"
  | "last_30_days"
  | "this_quarter"
  | "this_year";

export interface FilterOptions {
  period: PeriodFilter;
  pipelineId: string; // "all" or specific pipeline id
  salesId: string; // "all" or specific sales id
}

export const filterDeals = (deals: Deal[], filters: FilterOptions): Deal[] => {
  const now = new Date();
  let interval: { start: Date; end: Date } | null = null;

  if (filters.period === "this_month") {
    interval = { start: startOfMonth(now), end: endOfMonth(now) };
  } else if (filters.period === "last_30_days") {
    interval = { start: subDays(now, 30), end: now };
  } else if (filters.period === "this_quarter") {
    interval = { start: startOfQuarter(now), end: now };
  } else if (filters.period === "this_year") {
    interval = { start: startOfYear(now), end: now };
  }

  return deals.filter((deal) => {
    // Filtro por funil
    if (filters.pipelineId !== "all") {
      const dealFunil = String(deal.funil_id || "")
        .toLowerCase()
        .trim();
      const targetFunil = filters.pipelineId.toLowerCase().trim();
      if (dealFunil !== targetFunil) {
        return false;
      }
    }

    // Filtro por vendedor
    if (filters.salesId !== "all") {
      if (String(deal.sales_id) !== String(filters.salesId)) {
        return false;
      }
    }

    // Filtro por período
    if (interval && deal.created_at) {
      try {
        const dealDate = new Date(deal.created_at);
        if (!isWithinInterval(dealDate, interval)) {
          return false;
        }
      } catch {
        // Ignora data inválida
      }
    }

    return true;
  });
};

export const isDealWon = (
  deal: Deal,
  wonStages: string[] = ["won", "venda"],
): boolean => {
  if (deal.status === "ganho") return true;
  const stage = (deal.stage || "").toLowerCase();
  return wonStages.some((s) => s.toLowerCase() === stage);
};

export const isDealLost = (
  deal: Deal,
  lostStages: string[] = ["lost", "perdido"],
): boolean => {
  if (deal.status === "perdido") return true;
  const stage = (deal.stage || "").toLowerCase();
  return lostStages.some((s) => s.toLowerCase() === stage);
};

export const isDealOpen = (
  deal: Deal,
  wonStages: string[] = ["won", "venda"],
  lostStages: string[] = ["lost", "perdido"],
): boolean => {
  return !isDealWon(deal, wonStages) && !isDealLost(deal, lostStages);
};

export const formatCurrency = (val: number, currency = "BRL"): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(val || 0);
};

export const formatPercent = (val: number): string => {
  return `${Math.round(val || 0)}%`;
};
